import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, FolderPlus, Pencil, Search, Trash2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { FolderTree, pathTo } from "@/components/docs/FolderTree";
import { DocumentDialog } from "@/components/docs/DocumentDialog";
import { UploadDialog } from "@/components/docs/UploadDialog";

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [openDoc, setOpenDoc] = useState(null);
  const [upload, setUpload] = useState(false);

  const loadFolders = () => api.get("/folders").then((r) => {
    setFolders(r.data);
    setSelected((s) => s || r.data.find((f) => !f.parent_id)?.id || null);
  });
  useEffect(() => { loadFolders(); }, []);

  useEffect(() => {
    if (!selected && !q) return;
    const t = setTimeout(() => {
      api.get("/documents", { params: q ? { q } : { folder_id: selected } }).then((r) => setDocs(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [selected, q]);

  const folder = folders.find((f) => f.id === selected);

  const newFolder = async (parent) => {
    const name = window.prompt(parent ? `New sub-folder in "${parent.name}"` : "New top-level folder name");
    if (!name) return;
    try {
      const { data } = await api.post("/folders", { name, parent_id: parent?.id || null });
      await loadFolders();
      setSelected(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async () => {
    const name = window.prompt("Rename folder", folder.name);
    if (!name) return;
    await api.patch(`/folders/${folder.id}`, { name });
    loadFolders();
  };
  const removeFolder = async () => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try {
      await api.delete(`/folders/${folder.id}`);
      setSelected(folder.parent_id);
      loadFolders();
      toast.success("Folder deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const docChanged = (d) => { setDocs((x) => x.map((y) => (y.id === d.id ? d : y))); setOpenDoc(d); };
  const docAdded = (d) => { setDocs((x) => [...(x || []), d]); loadFolders(); };
  const docDeleted = (d) => { setDocs((x) => x.filter((y) => y.id !== d.id)); setOpenDoc(null); loadFolders(); };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Library</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Documents</h1>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search all documents" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" data-testid="doc-search-input" />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <aside className="rounded-2xl border bg-card p-3 lg:col-span-4 xl:col-span-3">
          <div className="mb-2 flex items-center justify-between px-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Folders</span>
            {isAdmin && <button onClick={() => newFolder(null)} data-testid="new-root-folder-btn" className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground" title="New folder"><FolderPlus className="h-4 w-4" /></button>}
          </div>
          <div className="max-h-[40vh] overflow-y-auto lg:max-h-none"><FolderTree folders={folders} selected={selected} onSelect={(id) => { setQ(""); setSelected(id); }} /></div>
        </aside>

        <section className="rounded-2xl border bg-card lg:col-span-8 xl:col-span-9" data-testid="documents-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
            <div className="min-w-0 text-sm">
              {q ? <span className="font-semibold">Search results for “{q}”</span> : (
                <span data-testid="folder-breadcrumb" className="font-semibold">{pathTo(folders, selected).map((f) => f.name).join(" / ") || "Select a folder"}</span>
              )}
            </div>
            {isAdmin && folder && !q && (
              <div className="flex flex-wrap gap-1">
                <button onClick={() => setUpload(true)} data-testid="upload-doc-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"><Upload className="h-4 w-4" /> Upload</button>
                <button onClick={() => newFolder(folder)} data-testid="new-subfolder-btn" className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="New sub-folder"><FolderPlus className="h-4 w-4" /></button>
                <button onClick={rename} data-testid="rename-folder-btn" className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Rename"><Pencil className="h-4 w-4" /></button>
                <button onClick={removeFolder} data-testid="delete-folder-btn" className="rounded-full p-2 text-red-400 hover:bg-red-500/10" title="Delete folder"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          {docs?.length === 0 && <p className="px-5 py-12 text-sm text-muted-foreground">No documents here yet.</p>}
          {docs?.map((d) => (
            <button key={d.id} onClick={() => setOpenDoc(d)} data-testid={`doc-row-${d.id}`} className="flex w-full items-center gap-4 border-b px-5 py-4 text-left last:border-0 hover:bg-secondary/50">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{d.name}</div>
                <div className="truncate text-xs text-muted-foreground">Updated {fmtDate(d.updated_at)} · {d.versions[d.versions.length - 1].uploaded_by}</div>
              </div>
              <span className="rounded-full border px-2.5 py-0.5 font-mono text-xs text-primary">v{d.current_version}</span>
            </button>
          ))}
        </section>
      </div>
      {openDoc && <DocumentDialog doc={openDoc} isAdmin={isAdmin} onClose={() => setOpenDoc(null)} onChange={docChanged} onDelete={docDeleted} />}
      {upload && folder && <UploadDialog folder={folder} onClose={() => setUpload(false)} onSaved={docAdded} />}
    </div>
  );
}
